// =============================================================================
// ConfirmDialog, a calm, branded replacement for the native `window.confirm`.
//
// Why:
//   • The native dialog renders as an ugly OS-level box (light/dark theme
//     mismatch, no 2120 branding, jarring on mobile). It also can't speak the
//     same voice as the rest of the product.
//
// How to use:
//   1.  Wrap your app once with <ConfirmDialogProvider> (see App.js).
//   2.  In any component:
//         const confirm = useConfirm();
//         const ok = await confirm({
//           title: 'End this user\'s trial?',
//           description: 'They\'ll lose Horizon access immediately.',
//           confirmLabel: 'End trial',
//           tone: 'destructive', // optional
//         });
//         if (!ok) return;
//
// The promise resolves `true` when the user confirms, `false` on cancel /
// escape / outside-click. Always safe to await; never throws.
// =============================================================================
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const Ctx = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  tone: 'default', // 'default' | 'destructive'
};

export function ConfirmDialogProvider({ children }) {
  const [opts, setOpts] = useState(null);
  const [open, setOpen] = useState(false);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpts({ ...DEFAULTS, ...options });
      setOpen(true);
    });
  }, []);

  const finish = (value) => {
    setOpen(false);
    // Drain the resolver on the next tick so the dialog has time to animate
    // out before the caller's `await` resumes (prevents UI flicker).
    setTimeout(() => {
      const r = resolverRef.current;
      resolverRef.current = null;
      r?.(value);
    }, 0);
  };

  const destructive = opts?.tone === 'destructive';

  return (
    <Ctx.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => { if (!v) finish(false); }}>
        <AlertDialogContent
          className="rounded-2xl"
          data-testid="confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="confirm-dialog-title">
              {opts?.title || DEFAULTS.title}
            </AlertDialogTitle>
            {opts?.description && (
              <AlertDialogDescription data-testid="confirm-dialog-description">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => finish(false)}
              data-testid="confirm-dialog-cancel"
              className="rounded-xl"
            >
              {opts?.cancelLabel || DEFAULTS.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => finish(true)}
              data-testid="confirm-dialog-confirm"
              className={`rounded-xl ${destructive
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : ''}`}
            >
              {opts?.confirmLabel || DEFAULTS.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(Ctx);
  if (!fn) {
    // Soft fallback so a misconfigured provider doesn't crash the page  
    // we degrade to native confirm and log a warning once.
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('useConfirm() called outside <ConfirmDialogProvider>. Falling back to window.confirm.');
    }
    return async (opts = {}) => {
      // eslint-disable-next-line no-alert
      return Promise.resolve(window.confirm(opts.title || opts.description || 'Are you sure?'));
    };
  }
  return fn;
}
